<script setup>
import { computed, inject, ref, onBeforeMount, nextTick } from 'vue';
import { debounce } from 'lodash';
import BaseListbox from '@/app/components/shared/base-listbox.vue';
import BaseListboxOption from '@/app/components/shared/base-listbox-option.vue';
import services from '../../services/autosuggest-search.js';

const config = inject('config');
const gaPush = inject('gaPush');

const props = defineProps({
  isHeaderSearch: {
    type: Boolean,
    required: true,
  },
  langCode: {
    type: String,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
});

const searchTerm = ref('');
const searchPredictions = ref([]);
const requestController = ref(null);
const firstSearchBtnClick = ref(false);
const listboxRef = ref(null);

const listboxId = ref('search-suggestions');
const currentSelectionIndex = ref(-1);
const setSize = computed(() => searchPredictions.value.length);
const isSearchTermMinimum3Chars = computed(() => searchTerm.value.length >= 3);
const gaPrefix = computed(() => (props.isHeaderSearch ? 'header' : 'component'));
const showRequiredMessage = computed(() => firstSearchBtnClick.value && !searchTerm.value.length);

const keyupHandler = debounce((event) => {
  isSearchTermMinimum3Chars.value ? fetchSuggestions(event) : (searchPredictions.value = []);
}, 200);

function fetchSuggestions(event) {
  if (event.keyCode !== 38 && event.keyCode !== 40 && event.keyCode !== 13) {
    if (searchTerm.value) {
      useRequestController();
      return services
        .searchPredictions(searchTerm.value, props.langCode)
        .then((response) => {
          currentSelectionIndex.value = -1;
          requestController.value = null;
          if (response.data.searchPredictions?.length) {
            searchPredictions.value = response.data.searchPredictions;
            nextTick(() => {
              listboxRef.value.listboxRef.scrollTop = 0;
            });
          } else {
            searchPredictions.value = [];
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      searchPredictions.value = [];
    }
  }
}

function useRequestController() {
  if (requestController.value) {
    requestController.value.abort();
  }
  requestController.value = new AbortController();
}

function setSelection(selection) {
  searchTerm.value = selection;
  searchPredictions.value = [];
  currentSelectionIndex.value = -1;

  gaPush({
    event: 'search_predictive_search_select',
    search_predictive_search_term: searchTerm.value,
  });
}

function enterHandler(event) {
  if (currentSelectionIndex.value > -1) {
    event.preventDefault();
    setSelection(searchPredictions.value[currentSelectionIndex.value]);
  }
}

function decrementSelectionIndex() {
  if (!setSize.value) return;
  if (currentSelectionIndex.value > 0) {
    currentSelectionIndex.value--;
  } else {
    currentSelectionIndex.value = setSize.value - 1;
  }
  optionInViewCheck();
}

function incrementSelectionIndex() {
  if (!setSize.value) return;
  if (currentSelectionIndex.value < setSize.value - 1) {
    currentSelectionIndex.value++;
  } else {
    currentSelectionIndex.value = 0;
  }
  optionInViewCheck();
}

function optionInViewCheck() {
  const listbox = listboxRef.value.listboxRef;
  const selectedOption = listbox.children[currentSelectionIndex.value];

  const selectedOptionRect = selectedOption.getBoundingClientRect();
  const listboxRect = listbox.getBoundingClientRect();

  const isTopVisible = selectedOptionRect.top >= listboxRect.top;
  const isBottomVisible = selectedOptionRect.bottom <= listboxRect.bottom;

  if (!isBottomVisible) {
    listbox.scrollTop = selectedOption.offsetTop - listboxRect.height + selectedOptionRect.height;
  } else if (!isTopVisible) {
    listbox.scrollTop = selectedOption.offsetTop;
  }
}

const vClickOutside = {
  mounted: function (el, binding) {
    el.clickOutsideEvent = function (event) {
      if (!(el == event.target || el.contains(event.target))) {
        binding.value(event, el);
      }
    };
    document.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted: function (el) {
    document.removeEventListener('click', el.clickOutsideEvent);
  },
};

function onClickOutside() {
  searchPredictions.value = [];
}

function handleFormSubmit(e) {
  firstSearchBtnClick.value = true;

  !searchTerm.value.length
    ? e.preventDefault()
    : gaPush({
        event: 'search_search_click',
        search_search_term: searchTerm.value,
        element_location: gaPrefix.value,
      });
}

onBeforeMount(() => {
  const url = new URL(window.location.href);
  const searchParam = url.searchParams.get('search');
  searchParam && !props.isHeaderSearch && (searchTerm.value = searchParam);
});
</script>

<template>
  <div
    class="c-search__wrapper"
    :class="{
      'c-search__wrapper--component': !props.isHeaderSearch,
      'c-search__wrapper--header': props.isHeaderSearch,
    }"
  >
    <form
      class="c-search"
      @submit="handleFormSubmit"
      :action="`/${props.langCode}/search-results#articlesResults`"
      role="search"
    >
      <div class="c-search__contain">
        <label class="c-search__label" :for="props.id">{{ config.autosuggestSearchTitle }}</label>
        <div class="c-search__contain-input-wrapper" v-click-outside="onClickOutside">
          <input
            class="c-search__contain-input"
            :id="id"
            type="text"
            autocomplete="off"
            name="search"
            v-model="searchTerm"
            :aria-owns="listboxId"
            :aria-describedby="`${props.id}-error`"
            @keydown.enter="enterHandler"
            @keyup="keyupHandler"
            @keydown.down.prevent="incrementSelectionIndex"
            @keydown.up.prevent="decrementSelectionIndex"
            :placeholder="config.autosuggestSearchPlaceholder"
          />
          <BaseListbox
            v-if="setSize"
            classPrefix="c-search__"
            :id="listboxId"
            @focusout="onClickOutside"
            ref="listboxRef"
          >
            <BaseListboxOption
              v-for="(option, optionIndex) in searchPredictions"
              @click="setSelection(option)"
              classPrefix="c-search__"
              :key="optionIndex"
              :index="optionIndex"
              :currentIndex="currentSelectionIndex"
              :setSize="setSize"
            >
              {{ option }}
            </BaseListboxOption>
          </BaseListbox>
        </div>
        <span :id="`${props.id}-error`" class="c-search__contain-input-error" aria-live="assertive">
          <span v-if="showRequiredMessage">{{ config.autosuggestSearchRequiredMessage }}</span>
        </span>
        <button class="c-search__contain-cta" type="submit">
          {{ config.autosuggestSearchButtonText }}
        </button>
      </div>
    </form>
  </div>
</template>
